import React, {useEffect, useState} from "react";
import clsx from "clsx";
import {FaFile, FaMobileAlt} from "react-icons/fa";
import {formatCaseCard} from "../utils/dashboard-formatters.js";
import {getGarrisonType} from "../utils/dashboard-filters.js";
import {getAssetPath} from "../../../../utils/get-asset-path.js";
import axios from "axios";
import {useDashboardContext} from "../context/dashboard-context.js";
import {vehicleIcon} from "../../../../components/vehicle-icon/vehicle-icon.js";

const svgIconRequests = new Map();

const GarrisonCard = ({garrison, serverDate}) => {
    const {cardInteractions, cardInformationHandlers} = useDashboardContext();
    const [svgIcons, setSvgIcons] = useState({});
    const hasValue = value => value !== null && value !== undefined;

    const status = getGarrisonType(garrison);
    const interactionCard = {
        type: "garrison",
        id: garrison.garrisonId,
        state: status,
        item: garrison,
    };
    const VehicleIcon = vehicleIcon[garrison.garrisonVehicleType];
    const startDate = garrison.dispatchStart ? new Date(garrison.dispatchStart) : null;
    const endDate = serverDate ? new Date(serverDate) : new Date();
    const elapsed = startDate && !Number.isNaN(startDate.getTime()) && !Number.isNaN(endDate.getTime())
        ? (() => {
            const diffMinutes = Math.max(0, Math.floor((endDate.getTime() - startDate.getTime()) / 60000));
            const days = Math.floor(diffMinutes / 1440);
            const hours = Math.floor((diffMinutes % 1440) / 60);
            const minutes = diffMinutes % 60;
            if (days > 0) return `${String(days).padStart(2, "0")}d ${String(hours).padStart(2, "0")}h`;
            if (hours > 0) return `${String(hours).padStart(2, "0")}h ${String(minutes).padStart(2, "0")}min`;
            return `${String(minutes).padStart(2, "0")}min`;
        })()
        : "";
    const dispatchNature = garrison.dispatchNature === "APOIO À GUARNIÇÃO" ? "Apoio" : (garrison.dispatchNature || garrison.garrisonIntendedFunction || "");
    const sourceLetter = garrison.dispatchId
        ? garrison.dispatchSource === "WEB_GENERATED" ? "C" : garrison.dispatchSource === "SELF_GENERATED" ? "A" : garrison.dispatchSource === "LEADER_GENERATED" ? "R" : ""
        : "";
    const hasSupport = Number(garrison.amountOfSupport ?? 0) > 0;
    const isMobileUnread = garrison.createdByMobile === true && !hasValue(garrison.byMobileAckDt);
    const garrisonIntegrants = Array.isArray(garrison.garrisonIntegrants)
        ? garrison.garrisonIntegrants
        : (garrison.garrisonIntegrants ? [String(garrison.garrisonIntegrants)] : []);
    const formattedPlace = formatCaseCard(garrison.place || garrison.dispatchAddress);
    const formattedSectors = formatCaseCard(garrison.garrisonSectors);
    const formattedVehicleClazz = formatCaseCard(garrison.garrisonVehicleClazz);
    const formattedTeam = formatCaseCard(garrison.garrisonTeam);
    const deviceMessage = hasValue(garrison.systemDtRead)
        ? {className: "card-icon-viewer icon-viewer-visualized float-end", icon: "viewer"}
        : hasValue(garrison.systemDtReceive)
            ? {className: "card-icon-viewer float-end", icon: "viewer"}
            : hasValue(garrison.systemDtSend)
                ? {className: "card-icon-view float-end", icon: "view"}
                : null;
    const deviceMessageIconName = deviceMessage?.icon;
    const hasNoSignal = garrison.garrisonHasMobile === true && !isMobileUnread && (garrison.mobileSignal === false || garrison.gpsOff === true);
    const composition = garrison.garrisonVehicleType !== "FOOT" ? "WITH_VEHICLE" : "WITHOUT_VEHICLE";

    useEffect(() => {
        let isMounted = true;
        const iconNames = [
            deviceMessageIconName,
            hasNoSignal ? "phone-no-signal" : null,
            isMobileUnread ? "phone-new" : null,
            garrison.garrisonHasAssignment ? "sw-assignment" : null,
        ].filter(Boolean);

        iconNames.forEach((iconName) => {
            if (!svgIconRequests.has(iconName)) {
                const request = axios
                    .get(getAssetPath(`img/dispatch-dashboard-svg/${iconName}.svg`), {responseType: "text"})
                    .then(({data}) => typeof data === "string" ? data : data?.documentElement?.outerHTML ?? "")
                    .catch((err) => {
                        svgIconRequests.delete(iconName);
                        throw err;
                    });

                svgIconRequests.set(iconName, request);
            }

            svgIconRequests.get(iconName)
                .then((svg) => {
                    if (!isMounted) return;
                    setSvgIcons((current) => ({...current, [iconName]: svg}));
                })
                .catch(() => {
                    if (!isMounted) return;
                    setSvgIcons((current) => ({...current, [iconName]: ""}));
                });
        });

        return () => {
            isMounted = false;
        };
    }, [deviceMessageIconName, hasNoSignal, isMobileUnread, garrison.garrisonHasAssignment]);

    return (
        <div
            id={`garrisonPanel_${garrison.garrisonId}`}
            className={clsx("garrison-cards-panel", cardInteractions.getDropClassName(interactionCard))}
            data-card-type="garrison"
            data-card-state={status}
            data-card-id={garrison.garrisonId}
            data-garrison-id={garrison.garrisonId ?? ""}
            data-garrison-type={status}
            data-dispatch-id={garrison.dispatchId ?? ""}
            data-attendance-id={garrison.attendanceId ?? ""}
            data-support-for-dispatch={garrison.supportForGarrisonDispatchId ?? ""}
            data-dispatch-source={garrison.dispatchSource ?? ""}
            data-intended-function={garrison.garrisonIntendedFunction ?? ""}
            data-composition={composition}
            draggable
            onClick={() => cardInteractions.selectCard(interactionCard)}
            onDoubleClick={() => cardInformationHandlers.openGarrisonInformation(garrison)}
            onContextMenu={event => cardInteractions.openContextMenu(event, interactionCard)}
            onDragStart={event => cardInteractions.handleDragStart(event, interactionCard)}
            onDragEnd={cardInteractions.handleDragEnd}
            onDragOver={event => cardInteractions.handleDragOver(event, interactionCard)}
            onDragLeave={() => cardInteractions.handleDragLeave(interactionCard)}
            onDrop={event => cardInteractions.handleDrop(event, interactionCard)}
        >
            <div
                id={`garrisonPanelSelection_${garrison.garrisonId}`}
                className={clsx("garrisons-card", status, cardInteractions.getSelectionClassName(interactionCard))}
            >
                <div className={`panel-heading-card ${status}`} />
                <div className={`panel-title-card ${status}`}>
                    <div className="flex-card min-h-5.25">
                        <div className="card-panel-body title-card-garrison">
                            <span className={"truncate block w-48.25"}>{garrison.dispatchStatus ? dispatchNature : (garrison.garrisonIntendedFunction || "")}</span>
                        </div>
                        <div className="flex items-center ml-auto">
                            {garrison.garrisonHasAssignment && svgIcons["sw-assignment"] &&
                                <span title="Atribuições" className="card-icon-assignment inline-flex items-center leading-none mr-1 align-middle relative top-px"
                                    dangerouslySetInnerHTML={{__html: svgIcons["sw-assignment"]}}/>
                            }
                            {sourceLetter ? <div className="pull-left"><span className="dispatch-source-icon">{sourceLetter}</span></div> : null}
                            {isMobileUnread && svgIcons["phone-new"] &&
                                <div className="pull-left" style={{margin: "-5px 2px 0 2px"}}>
                                    <span className="card-icon-phone-new" dangerouslySetInnerHTML={{__html: svgIcons["phone-new"]}}/>
                                </div>
                            }
                            {
                                hasNoSignal && svgIcons["phone-no-signal"] &&
                                <div className="pull-left" style={{margin: "0px 2px 0px 2px"}}>
                                    <span className="card-icon-phone-nosignal" dangerouslySetInnerHTML={{__html: svgIcons["phone-no-signal"]}}/>
                                </div>
                            }
                            {garrison.garrisonHasMobile === true && !isMobileUnread && garrison.mobileSignal === true ?
                                <div className="pull-left flex items-center" style={{marginLeft: 5, marginTop: 1, height: 20}}><FaMobileAlt size={13}/></div> : null}
                        </div>
                    </div>
                </div>
                <div className={`garrisons-cards-body ${status}`}>
                    <div className="flex-card">
                        <div className="card-panel-body">
                            <span className="font-number-card">{garrison.dispatchStatus ? garrison.checkbookNumber : ""}</span><br/>
                        </div>
                        <div className="card-panel-body">
                            <span>
                                {
                                    garrison.occurrenceNumber
                                    ? <span className={"flex gap-0.5 items-center"}><FaFile size={11} className={"mb-0.5"}/><span className="font-number-card">{garrison.occurrenceNumber}</span></span>
                                    : ""
                                }
                            </span>
                        </div>
                        <div className="card-panel-body">
                            <span className="float-end">{garrison.dispatchStatus ? elapsed : ""}</span>
                        </div>
                    </div>
                    <div className="flex-card">
                        <div className="card-panel-body">
                            <span className={"truncate"}>{formattedPlace}</span><br/>
                        </div>
                    </div>
                    <div className="flex-card">
                        <div className="card-panel-body">
                            <span className={"truncate"}>{formattedSectors}</span>
                        </div>
                        <div className="card-panel-body">
                            <span className="float-end truncate">{formattedVehicleClazz}</span><br/>
                        </div>
                    </div>
                    <div className="flex-card">
                        <div className="card-panel-body" title={garrisonIntegrants.join(", ")}>
                            <span className={"truncate"}>{garrison.contingentDisplay && garrison.contingentDisplay !== "null" ? garrison.contingentDisplay : (garrison.firstIntegrant || "")}</span>
                        </div>
                        <div className="card-panel-body">
                            <span className="float-end truncate">{formattedTeam}</span>
                        </div>
                    </div>
                    <div className="flex-card">
                        <div className="card-panel-body flex items-center gap-1.25">
                            <span>{VehicleIcon ? <VehicleIcon size={14}/> : null}</span>
                            <span>
                                {garrison.garrisonIdentification ? <span className="garrisons-cards-garrisonIdentification">{garrison.garrisonIdentification}</span> : null}
                                {garrison.garrisonIdentification && garrison.garrisonVehiclePlate ? <span style={{marginLeft: 1}}>| {garrison.garrisonVehiclePlate}</span> : null}
                                {!garrison.garrisonIdentification && garrison.garrisonVehiclePlate ? <span className="garrisons-cards-garrisonIdentification">{garrison.garrisonVehiclePlate}</span> : null}
                            </span>
                            {hasSupport && <span className="bg-white text-[#666666] rounded-xs py-px px-0.75 border border-[#666666] leading-3.5">+{garrison.amountOfSupport}</span>}
                        </div>
                        <div className="card-panel-body">
                            {
                                deviceMessage && svgIcons[deviceMessageIconName] &&
                                <span
                                    className={deviceMessage.className}
                                    dangerouslySetInnerHTML={{__html: svgIcons[deviceMessageIconName]}}
                                />
                            }
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GarrisonCard;
